import React, { useState, useEffect } from 'react'
import { AlertCircle, X, RefreshCw, Wifi, WifiOff } from 'lucide-react'

export interface NotificationError {
    id: string
    title: string
    message: string
    type: 'error' | 'warning' | 'info' | 'network' | 'success'
    isRetryable?: boolean
    onRetry?: () => void
    autoHide?: boolean
    duration?: number
}

interface ErrorNotificationProps {
    errors: NotificationError[]
    onDismiss: (id: string) => void
    position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center'
}

const ErrorNotification: React.FC<ErrorNotificationProps> = ({
    errors,
    onDismiss,
    position = 'top-right'
}) => {
    const [isOnline, setIsOnline] = useState(navigator.onLine)

    useEffect(() => {
        const handleOnline = () => setIsOnline(true)
        const handleOffline = () => setIsOnline(false)

        window.addEventListener('online', handleOnline)
        window.addEventListener('offline', handleOffline)

        return () => {
            window.removeEventListener('online', handleOnline)
            window.removeEventListener('offline', handleOffline)
        }
    }, [])

    // Auto-hide notifications
    useEffect(() => {
        errors.forEach(error => {
            if (error.autoHide !== false && error.duration !== 0) {
                const duration = error.duration || 5000
                const timer = setTimeout(() => {
                    onDismiss(error.id)
                }, duration)

                return () => clearTimeout(timer)
            }
        })
    }, [errors, onDismiss])

    const getPositionClasses = () => {
        switch (position) {
            case 'top-left':
                return 'top-4 left-4'
            case 'top-center':
                return 'top-4 left-1/2 transform -translate-x-1/2'
            case 'top-right':
                return 'top-4 right-4'
            case 'bottom-left':
                return 'bottom-4 left-4'
            case 'bottom-right':
                return 'bottom-4 right-4'
            default:
                return 'top-4 right-4'
        }
    }

    const getTypeIcon = (type: NotificationError['type']) => {
        switch (type) {
            case 'network':
                return isOnline ? <Wifi className="h-5 w-5" /> : <WifiOff className="h-5 w-5" />
            case 'error':
            case 'warning':
            case 'info':
            default:
                return <AlertCircle className="h-5 w-5" />
        }
    }

    const getTypeClasses = (type: NotificationError['type']) => {
        switch (type) {
            case 'error':
                return 'bg-white border-specialRed text-specialRed shadow-lg'
            case 'warning':
                return 'bg-white border-specialYellow text-specialYellow shadow-lg'
            case 'info':
                return 'bg-white border-kibuBlue text-kibuBlue shadow-lg'
            case 'success':
                return 'bg-white border-green text-green shadow-lg'
            case 'network':
                // Network errors should always be red - green is for success notifications
                return 'bg-white border-specialRed text-specialRed shadow-lg'
            default:
                return 'bg-white border-grey text-gray-800 shadow-lg'
        }
    }

    const getButtonClasses = (type: NotificationError['type']) => {
        switch (type) {
            case 'error':
                return 'text-specialRed hover:text-white hover:bg-specialRed'
            case 'warning':
                return 'text-specialYellow hover:text-white hover:bg-specialYellow'
            case 'info':
                return 'text-kibuBlue hover:text-white hover:bg-kibuBlue'
            case 'success':
                return 'text-green hover:text-white hover:bg-green'
            case 'network':
                // Network errors should always be red - green is for success notifications
                return 'text-specialRed hover:text-white hover:bg-specialRed'
            default:
                return 'text-gray-600 hover:text-white hover:bg-gray-800'
        }
    }

    if (errors.length === 0) return null

    return (
        <div className={`fixed ${getPositionClasses()} z-[9999] space-y-2 max-w-sm w-full`}>
            {errors.map((error) => (
                <div
                    key={error.id}
                    className={`
                        ${getTypeClasses(error.type)}
                        rounded-lg border p-4 shadow-lg
                        animate-in slide-in-from-top-2 fade-in duration-300
                    `}
                >
                    <div className="flex items-start">
                        <div className="flex-shrink-0">
                            {getTypeIcon(error.type)}
                        </div>

                        <div className="ml-3 flex-1">
                            <h3 className="text-sm font-semibold mb-1">
                                {error.title}
                            </h3>
                            <p className="text-sm opacity-90">
                                {error.message}
                            </p>

                            {error.isRetryable && error.onRetry && (
                                <button
                                    onClick={error.onRetry}
                                    className={`
                                        ${getButtonClasses(error.type)}
                                        mt-2 text-xs font-medium px-2 py-1 rounded
                                        transition-colors flex items-center gap-1
                                    `}
                                >
                                    <RefreshCw className="h-3 w-3" />
                                    Try Again
                                </button>
                            )}
                        </div>

                        <button
                            onClick={() => onDismiss(error.id)}
                            className={`
                                ${getButtonClasses(error.type)}
                                flex-shrink-0 p-1 rounded-full
                                transition-colors
                            `}
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            ))}
        </div>
    )
}

export default ErrorNotification